<template>
  <div id="app">
    <transition appear @enter="enterLoader" @leave="leaveLoader" mode="in-out">
      <section class="loader" v-if="isLoading">
        <article @click="hideLoader">
          <q data-text class="loader__explanation" :data-subline="!isAnimating ? 'active' : ''">
            <b>One World One Face</b> ought to <br class="mobile--visible">represent everyone <br class="mobile--hidden">without talking <br class="mobile--visible">about anyone in particular.<br>
            <span class="text--underline"><span class="text__line text__line--1"></span>Community</span>, 
            <span class="text--underline"><span class="text__line text__line--2"></span>Unity</span>, 
            <span class="text--underline"><span class="text__line text__line--3"></span>Equality</span> 
            and <span class="text--underline"><span class="text__line text__line--4"></span>Creativity</span> 
            are the core <br class="mobile--hidden">values on which <br class="mobile--visible">the project is built.

            <span class="loader__caption">
              It starts from <span data-underscore><span></span>here</span>.
            </span>
          </q>
        </article>
      </section>
    </transition>

    <header>
      <HeaderComp v-show="loaderIsOut" :loaderIsOut="loaderIsOut" />
    </header>

    <main class="content">
      <transition
        @enter="transitionEnter"
        @leave="transitionLeave"
        mode="out-in"
      >
        <router-view v-if="loaderIsOut" :key="$route.path"></router-view>
      </transition>
    </main>

    <div
      class="transition-cover"
      :style="{ background: 'radial-gradient(ellipse at center, rgba(0,0,0,0) 0%, rgba(0,0,0,0) 0%)'}"
    ></div>

    <cookie-law theme="min" buttonText="Ok" transitionName="fade" buttonClass="cookie-button">
      <div slot="message">
        This website uses cookies. <br v-if="isMobile()"><a href="https://www.iubenda.com/privacy-policy/31537728/cookie-policy" target="_blank" rel="noopener">Learn More.</a>
      </div>
    </cookie-law>

    <Pointer v-if="!isLoading" v-show="$route.path != '/add-your-face'" />
    <ScreenSaver v-if="loaderIsOut && $route.path != '/add-your-face'" />
    <Favicon v-if="loaderIsOut" />
  </div>
</template>

<script>
import CookieLaw from 'vue-cookie-law';

import HeaderComp from '@/components/HeaderComp';
import Pointer from '@/components/Cursor';
import ScreenSaver from '@/components/ScreenSaver';
import Favicon from '@/components/Favicon';

import transitions from '@/scripts/transitions';
import { isMobile } from '@/scripts/utils';


export default {
  name: 'app',

  components: {
    HeaderComp,
    Pointer,
    ScreenSaver,
    Favicon,
    CookieLaw,
  },

  data() {
    return {
      isAnimating: false,
      isLoading: this.$route.name !== 'ErrorPage',
      loaderIsOut: this.$route.name === 'ErrorPage',
      windowH: window.innerHeight,
      isMobile,
    };
  },

  mounted() {
    this.bindKeyboard();
  },

  methods: {
    hideLoader() {
      this.isLoading = false;
      document.removeEventListener('keydown', this.handleKey);
    },

    bindKeyboard() {
      this.handleKey = event => this.clickKey(event);
      document.addEventListener('keydown', this.handleKey);
    },

    clickKey(event) {
      if (event.which === 13) {
        this.hideLoader();
      }
    },

    enterLoader(el, done) {
      this.isAnimating = true;

      const callback = () => {
        this.isAnimating = false;
        done();
      };

      transitions.get('loaderTransitionIn')(el, callback);
    },

    leaveLoader(el, done) {
      this.isAnimating = true;

      const callback = () => {
        this.loaderIsOut = true;
        this.isAnimating = false;
        done();
      };

      transitions.get('loaderTransitionOut')(el, callback);
    },

    transitionEnter(el, done) {
      transitions.get('pageTransition')(el, 'in', done);
    },

    transitionLeave(el, done) {
      transitions.get('pageTransition')(el, 'out', done);
    },
  },
};
</script>

<style lang="scss">
@import '/styles/colors';
@import '/application';

header {
  transition: all 0.6s cubic-bezier(.17,.67,.32,.87);
}

header.hide {
  transform: translateY(-100%);
  // opacity: 0;
}
header.theme-dark {
  .header__container {

    &::before {
      transform-origin: 0 100%;
      transition: all 0.3s cubic-bezier(.17,.67,.32,.87) 0.3s;
      transform: scaleY(1);
    }
  }
}

body {
  position: relative;
  left: 0;
  width: 100%;
  height: 100vh;
  margin: 0;
  font-family: 'Neutral', sans-serif;
  font-weight: normal;

  overflow: hidden;
}

.gmnoprint {
  display: none;
}

.content {
  position: relative;
  width: 100%;
  height: 100vh;
  overflow: hidden;
}

header {
  position: fixed;
  left: 0;
  width: 100%;

  box-sizing: border-box;

  font-size: 1rem;

  z-index: z-index(header);

  @include mq(md) {
    font-size: .6875rem;
  }

  a {
    color: $color-light;
  }
}

header {
  top: 0;
  pointer-events: none;
  padding: 10px 20px;

  @include mq(md) {
    padding: 15px 50px;
  }

  .header__container {
    padding: 0px 20px;
    position: relative;

     @include mq(md) {
       padding: 5px 20px;
     }

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: #0e0e0e;
      
      transform: scaleY(0);
      transform-origin: 0 0%;
      transition: all 0.3s cubic-bezier(.17,.67,.32,.87) 0.3s;
    }
  }
}

.o-centerize-cont {
  position: absolute;
  width: 100%;
  height: 100%;
  max-width: 1280px;
  left: 0;
  right: 0;
  margin: auto;
  padding: 0 40px;
  box-sizing: border-box;
}

#video-canvas {
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  pointer-events: none;
}

.transition-cover {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  z-index: 50;
  pointer-events: none;

  background: radial-gradient(ellipse at center, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 0%);
}

div[lazy] {
  display: visible;
  transition: all 0.8s cubic-bezier(.17,.67,.32,.87);
  transition-delay: 0.5s;

  background: $color-black;
  opacity: 1;

  @include boost-performance(true);
}

div[lazy="loading"] {
  opacity: 1;
  transform: scale(1.5);
  clip-path: inset(0 100% 0 0);
}

div[lazy="loaded"] {
  opacity: 1;
  transform: scale(1);
  clip-path: inset(0 0 0 0);
}

@keyframes slideIn {
  0% {
    transform: scaleX(0);
    transform-origin: 0;
  }

  100% {
    transform: scaleX(1);
    transform-origin: 0;
  }
}

@keyframes popIn {
  0% {
    transform: scaleX(0) scaleY(0.2);
    transform-origin: 0 100%;
  }

  50% {
    transform: scaleX(1) scaleY(0.2);
    transform-origin: 0 100%;
  }

  100% {
    transform: scaleX(1) scaleY(1);
    transform-origin: 0 100%;
  }
}

.mobile {
  &--visible {
    @include mq(md) {
      display: none;
    }
  }

  &--hidden {
    display: none;
    @include mq(md) {
      display: inline-block;
    }
  }
}

.loader {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  text-align: center;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";

  background: radial-gradient(ellipse at center, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 0%);

  z-index: 1000;

  article {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;

    line-height: 1.8;
    color: $color-light;

    display: flex;
    align-items: center;
    justify-content: center;

    q {
      display: inline-block;
      padding: 0 40px;
      max-width: 610px;

      margin-bottom: 40px;

      &::before,
      &::after {
        content: "";
      }
    }

    b {
      display: inline-block;
    }
  }

  &__explanation {
    font-size: 16px;
    color: $color-gray;

    @include mq(sm) {
      font-size: 20px;
    }
  }

  &__caption {
    display: inline-block;
    width: 100%;
    font-size: 16px;
    font-weight: 500;
    transform: translateY(60px);
    color: $color-light;

    cursor: pointer;
  }

  q > span {
    display: inline-block;
  }

  [data-underscore] {
    position: relative;

    span {
      position: absolute;
      bottom: -1px;
      left: 0;
      width: 100%;
      height: 10px;

      background-color: $color-blue;

      z-index: -1;

      @include mq(sm) {
        transition: all 0.3s ease-out;
        transform: scaleX(0);
      }
    }

    @at-root .pointerevents:not(.touchevents) &:hover span,
    .no-pointerevents.no-touchevents &:hover span {
      height: 2px;
    }
  }

  [data-subline] {
    .text--underline {
      position: relative;
      display: inline-block;

      span {
        position: absolute;
        bottom: 3px;
        left: 0;
        width: 100%;
        height: 2px;

        background-color: $color-blue;

        @include mq(sm) {
          transform: scaleX(0);
        }
      }
    }
  }

  [data-subline="active"] {
    @include mq(sm) {
      .text__line--1 {
        animation: slideIn 0.5s 0s ease-in-out 1 forwards;
      }

      .text__line--2 {
        animation: slideIn 0.5s 0.1s ease-in-out 1 forwards;
      }

      .text__line--3 {
        animation: slideIn 0.5s 0.2s ease-in-out 1 forwards;
      }

      .text__line--4 {
        animation: slideIn 0.5s 0.3s ease-in-out 1 forwards;
      }

      [data-underscore] span {
        animation: popIn 1s 0.6s ease-in-out 1 forwards;
      }
    }
  }
}

.description-box__content a,
.introduction__content a,
.diary__col a,
.info__intro a {
  font-weight: 500;
  text-decoration: underline;
  color: $color-dark-gray;
  transition: color 0.3s cubic-bezier(0.215, 0.61, 0.355, 1);

  @at-root .pointerevents:not(.touchevents) &:hover,
  .no-pointerevents.no-touchevents &:hover {
    color: $color-blue;
  }
}

.info__intro a,
.diary__col--black a {
  color: $color-light;
}

.about-content--desktop {
  display: none;

  @include mq(sm) {
    display: inline-block;
  }
}

.about-content--mobile {
  display: inline-block;

  @include mq(sm) {
    display: none;
  }
}

.Cookie.Cookie--min {
  width: 100%;
  font-size: font-size(s);
  z-index: z-index(loader);
  color: $color-gray;
  display: flex;
  flex-flow: row;
  padding: 10px 20px 10px 30px;
  background: $color-black;
  font-size: 1em;

  @include mq(md) {
    font-size: 0.875em;
    max-width: 500px;
    width: 40%;
    margin: 20px auto 63px;
  }

  a {
    opacity: 0.8;
    font-weight: 500;
  }
}

.cookie-button {
  font-size: 1em;
  padding: 5px 20px;

  background: transparent;
  border: 2px solid $color-blue;
  color: $color-light;
  font-weight: 400;

  transition: color 0.3s cubic-bezier(0.215, 0.61, 0.355, 1);

  cursor: pointer;

  @at-root .pointerevents:not(.touchevents) &:hover,
  .no-pointerevents.no-touchevents &:hover {
    color: $color-blue;
  }

  @include mq(md) {
    font-size: 0.875em;
  }
}

[data-mouse] {
  display: inline-block;
}

.hero__figure {
  #video-canvas {
    top: 0;
    left: 50%;
    width: auto;
    height: 100%;
    transform: translate3d(-50%, 0, 0);

    pointer-events: none;

    @include mq(md) {
      left: 0;
      width: 100vw;
      transform: none;
      object-fit: cover;
      object-position: 50% 50%;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/App.vue